import {
  createContext,
  useContext,
  useCallback,
  useState,
  useEffect,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import en from "../../lib/data/en.json";
import pt from "../../lib/data/pt.json";
import {
  languageOptions,
  languageIsLoaded,
  loadLanguage,
} from "./data/translations";

const MarketingContext = createContext();
const valid = (code) => languageOptions.some((option) => option.code === code);
export function MarketingLanguageProvider({ children }) {
  const globalLanguage = useLanguage();
  const { pathname, search } = useLocation();
  const navigate = useNavigate();
  const [language, setLanguageState] = useState(() => {
    if (pathname === "/br") return "pt";
    const requested = new URLSearchParams(search).get("lang");
    if (valid(requested)) return requested;
    const saved = localStorage.getItem("marketing-language");
    return valid(saved) ? saved : globalLanguage.language;
  });
  const [, refreshLanguage] = useState(0);
  const [loadError, setLoadError] = useState(false);
  const [retry, setRetry] = useState(0);
  useEffect(() => {
    let alive = true;
    setLoadError(false);
    if (languageIsLoaded(language)) return;
    loadLanguage(language)
      .then(() => {
        if (alive) refreshLanguage((value) => value + 1);
      })
      .catch(() => {
        if (alive) setLoadError(true);
      });
    return () => {
      alive = false;
    };
  }, [language, retry]);
  const setLanguage = useCallback(
    (code) => {
      if (!valid(code)) return;
      localStorage.setItem("marketing-language", code);
      setLanguageState(code);
      // Existing account and dashboard translations remain English/Portuguese.
      if (code === "en" || code === "pt") globalLanguage.setLanguage(code);
      const params = new URLSearchParams(search);
      params.set("lang", code);
      navigate({
        pathname: ["/", "/br"].includes(pathname)
          ? code === "pt"
            ? "/br"
            : "/"
          : pathname,
        search: `?${params}`,
      });
    },
    [globalLanguage.setLanguage, pathname, search, navigate],
  );
  useEffect(() => {
    const requested =
      pathname === "/br" ? "pt" : new URLSearchParams(search).get("lang");
    if (valid(requested)) setLanguageState(requested);
  }, [pathname, search]);
  useEffect(() => {
    localStorage.setItem("marketing-language", language);
    if (language === "en" || language === "pt")
      globalLanguage.setLanguage(language);
  }, [language, globalLanguage.setLanguage]);
  const t = useCallback(
    (key) => {
      const keys = key.split(".");
      const read = (bundle) =>
        keys.reduce((value, part) => value?.[part], bundle);
      return read(language === "pt" ? pt : en) ?? read(en) ?? key;
    },
    [language],
  );
  if (!languageIsLoaded(language))
    return (
      <div
        style={{
          minHeight: "70vh",
          display: "grid",
          placeItems: "center",
          fontFamily: "Inter, sans-serif",
          color: "#087761",
        }}
      >
        {loadError ? (
          <div lang="en" role="alert">
            <p>This language could not be opened.</p>
            <button onClick={() => setRetry((value) => value + 1)}>
              Try again
            </button>{" "}
            · <button onClick={() => setLanguage("en")}>Show English</button>
          </div>
        ) : (
          <span lang={language} role="status">
            {languageOptions.find((option) => option.code === language)?.label}…
          </span>
        )}
      </div>
    );
  return (
    <MarketingContext.Provider
      value={{ language, setLanguage, t, languages: languageOptions }}
    >
      {children}
    </MarketingContext.Provider>
  );
}
export const useMarketingLanguage = () => useContext(MarketingContext);
